'use client';

import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import useMedia from 'react-use/lib/useMedia';

type BreakpointSingleValue = string | number;

type BreakpointValueObject<T extends BreakpointSingleValue> = {
    default: T;
    sm?: T;
    md?: T;
    lg?: T;
    xl?: T;
    '2xl'?: T;
};

type BreakpointValueKey<T extends BreakpointSingleValue> = keyof Omit<
    BreakpointValueObject<T>,
    'default'
>;

export type BreakpointValue<T extends BreakpointSingleValue> =
    | T
    | BreakpointValueObject<T>;

export const useBreakpoint = <T extends BreakpointSingleValue>(
    defaultBreakpoint?: keyof Omit<BreakpointValueObject<T>, 'default'>
) => {
    const isSm = useMedia('(min-width: 640px)', defaultBreakpoint === 'sm');
    const isMd = useMedia('(min-width: 768px)', defaultBreakpoint === 'md');
    const isLg = useMedia('(min-width: 1024px)', defaultBreakpoint === 'lg');
    const isXl = useMedia('(min-width: 1280px)', defaultBreakpoint === 'xl');
    const is2xl = useMedia('(min-width: 1536px)', defaultBreakpoint === '2xl');

    if (is2xl) {
        return '2xl';
    }

    if (isXl) {
        return 'xl';
    }

    if (isLg) {
        return 'lg';
    }

    if (isMd) {
        return 'md';
    }

    if (isSm) {
        return 'sm';
    }

    return 'default';
};

export const useBreakpointValue = <T extends BreakpointSingleValue>(
    valueProp?: BreakpointValue<T>,
    defaultBreakpoint?: keyof Omit<BreakpointValueObject<T>, 'default'>
): T | undefined => {
    const bp = useBreakpoint(defaultBreakpoint);

    const value = useMemo(() => {
        if (isNil(valueProp)) {
            return undefined;
        }

        if (typeof valueProp !== 'object') {
            return valueProp;
        }

        return ['default', 'sm', 'md', 'lg', 'xl', '2xl'].reduce(
            (current, key, index, self) =>
                index <= self.indexOf(bp) &&
                !isNil(valueProp[key as BreakpointValueKey<T>])
                    ? valueProp[key as BreakpointValueKey<T>]
                    : current,
            valueProp[bp]
        );
    }, [bp, valueProp]);

    return value;
};
